import React, { useState, useEffect, useRef, useCallback } from 'react';
import axios from 'axios';

const API = process.env.REACT_APP_BACKEND_URL + '/api';

const authHeaders = () => {
  const token = localStorage.getItem('token');
  return token ? { Authorization: `Bearer ${token}` } : {};
};

const STEP_ICON = {
  pending: '○',
  running: '◉',
  done: '✓',
  skipped: '–',
  failed: '!',
};

const COLOR_BAND = {
  rojo: { bg: 'bg-rose-600', soft: 'bg-rose-50', text: 'text-rose-700', ring: 'ring-rose-200' },
  naranja: { bg: 'bg-amber-500', soft: 'bg-amber-50', text: 'text-amber-700', ring: 'ring-amber-200' },
  amarillo: { bg: 'bg-yellow-500', soft: 'bg-yellow-50', text: 'text-yellow-700', ring: 'ring-yellow-200' },
  verde: { bg: 'bg-emerald-600', soft: 'bg-emerald-50', text: 'text-emerald-700', ring: 'ring-emerald-200' },
};

const GRAVEDAD_STYLE = {
  criticas: 'bg-rose-100 text-rose-800 border-rose-200',
  altas: 'bg-amber-100 text-amber-800 border-amber-200',
  medias: 'bg-yellow-100 text-yellow-800 border-yellow-200',
  bajas: 'bg-slate-100 text-slate-700 border-slate-200',
  informativas: 'bg-sky-100 text-sky-800 border-sky-200',
};

const FEED_STYLE = {
  ok: 'text-emerald-300',
  found: 'text-amber-300',
  warn: 'text-orange-300',
  error: 'text-rose-300',
  info: 'text-slate-300',
};

// Cuánto hace que arrancó el paso: sin esto, en el escaneo de puertos parece que se colgó.
const minutosDesde = (iso) => {
  if (!iso) return 'un momento';
  const min = Math.max(1, Math.round((Date.now() - new Date(iso).getTime()) / 60000));
  return min === 1 ? '1 minuto' : `${min} minutos`;
};

const Counter = ({ label, value, tone }) => (
  <div className={`rounded-xl border px-4 py-3 text-center ${tone}`} data-testid={`assisted-count-${label}`}>
    <div className="text-3xl font-bold leading-none">{value}</div>
    <div className="mt-1 text-xs font-medium uppercase tracking-wide">{label}</div>
  </div>
);

const AssistedCheck = () => {
  const [status, setStatus] = useState(null);
  const [targetId, setTargetId] = useState('');
  const [profile, setProfile] = useState('aplicaciones');
  const [view, setView] = useState('intro'); // intro | preflight | running | report
  const [check, setCheck] = useState(null);
  const [error, setError] = useState('');
  const [starting, setStarting] = useState(false);
  const [history, setHistory] = useState([]);
  const pollRef = useRef(null);
  const feedRef = useRef(null);

  const loadCheck = useCallback(async (checkId) => {
    const res = await axios.get(`${API}/assisted-check/${checkId}`, { headers: authHeaders() });
    setCheck(res.data);
    setView(res.data.status === 'running' ? 'running' : 'report');
    return res.data;
  }, []);

  const loadStatus = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/assisted-check/status`, { headers: authHeaders() });
      setStatus(res.data);
      setProfile(res.data.default_profile || 'aplicaciones');
      if (res.data.targets?.length) setTargetId(res.data.targets[0].id);
      if (res.data.running_check_id) await loadCheck(res.data.running_check_id);
      const hist = await axios.get(`${API}/assisted-check/history`, { headers: authHeaders() });
      setHistory(hist.data.checks || []);
    } catch (err) {
      setError(err.response?.data?.detail?.message || err.message);
    }
  }, [loadCheck]);

  useEffect(() => { loadStatus(); }, [loadStatus]);

  useEffect(() => {
    if (view !== 'running' || !check?.id) return undefined;
    pollRef.current = setInterval(async () => {
      try {
        const data = await loadCheck(check.id);
        if (data.status !== 'running') {
          clearInterval(pollRef.current);
          loadStatus();
        }
      } catch (err) { /* reintenta en el proximo tick */ }
    }, 4000);
    return () => clearInterval(pollRef.current);
  }, [view, check?.id, loadCheck, loadStatus]);

  useEffect(() => {
    if (feedRef.current) feedRef.current.scrollTop = feedRef.current.scrollHeight;
  }, [check?.feed?.length]);

  const start = async () => {
    setStarting(true);
    setError('');
    try {
      const res = await axios.post(`${API}/assisted-check/start`,
        { target_id: targetId, port_profile: profile }, { headers: authHeaders() });
      await loadCheck(res.data.check_id);
    } catch (err) {
      const d = err.response?.data?.detail;
      setError(typeof d === 'string' ? d : (d?.message || err.message));
    } finally {
      setStarting(false);
    }
  };

  if (!status) {
    return <div className="p-10 text-center text-gray-500" data-testid="assisted-loading">Cargando...</div>;
  }

  if (!status.enabled) {
    return (
      <div className="max-w-2xl mx-auto mt-10 rounded-2xl border border-slate-200 bg-white p-8 text-center shadow-sm"
           data-testid="assisted-not-enabled">
        <div className="text-5xl">🔒</div>
        <h2 className="mt-4 text-2xl font-bold text-slate-800">Chequeá la seguridad de tu empresa</h2>
        <p className="mt-3 text-slate-600">
          Es nuestro servicio asistido: con un solo botón revisamos todo y te entregamos un informe
          en castellano, sin que necesites gente de sistemas.
        </p>
        <p className="mt-4 text-sm text-slate-500">
          {!status.plan_ok
            ? 'Está disponible desde el plan Profesional en adelante.'
            : 'Todavía no está activado para tu empresa.'}{' '}
          Contactate con tu distribuidor para habilitarlo.
        </p>
      </div>
    );
  }

  const esperado = status.port_profiles.find((p) => p.id === profile)?.minutes || '15 a 20 minutos';
  const pasos = check?.steps || status.steps.map((s) => ({ ...s, status: 'pending' }));
  const hechos = pasos.filter((s) => ['done', 'skipped', 'failed'].includes(s.status)).length;
  const avance = Math.round((hechos / pasos.length) * 100);

  return (
    <div className="pb-16" data-testid="assisted-check">
      <style>{`@media print {
        body * { visibility: hidden; }
        #assisted-report, #assisted-report * { visibility: visible; }
        #assisted-report { position: absolute; left: 0; top: 0; width: 100%; }
        .no-print { display: none !important; }
      }`}</style>

      {/* ---------- Portada ---------- */}
      {view === 'intro' && (
        <div className="no-print">
          <div className="relative overflow-hidden rounded-3xl bg-gradient-to-br from-slate-900 via-slate-800 to-blue-900 px-8 py-14 text-center shadow-xl">
            <div className="absolute inset-0 opacity-20"
                 style={{ backgroundImage: 'radial-gradient(circle at 20% 20%, #38bdf8 0, transparent 45%), radial-gradient(circle at 80% 0%, #818cf8 0, transparent 40%)' }} />
            <div className="relative">
              <span className="inline-block rounded-full bg-white/10 px-4 py-1 text-xs font-semibold uppercase tracking-widest text-sky-200">
                Servicio asistido
              </span>
              <h1 className="mt-5 text-4xl sm:text-5xl font-extrabold text-white">
                Chequeá la seguridad de tu empresa
              </h1>
              <p className="mx-auto mt-5 max-w-2xl text-base text-slate-300">
                Un solo botón corre las {status.steps.length} revisiones de SegurIA sobre tus sistemas
                y te deja un informe claro, listo para imprimir. No hace falta que sepas de informática.
              </p>
              <button
                onClick={() => setView('preflight')}
                data-testid="assisted-open-preflight"
                className="mt-9 rounded-full bg-sky-400 px-10 py-4 text-lg font-bold text-slate-900 shadow-lg transition hover:bg-sky-300 hover:shadow-sky-500/30"
              >
                Empezar el chequeo
              </button>
              <p className="mt-4 text-xs text-slate-400">Tarda entre 10 y 50 minutos según la profundidad que elijas</p>
            </div>
          </div>

          <div className="mt-8 grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
            {status.steps.map((s) => (
              <div key={s.key} className="rounded-xl border border-slate-200 bg-white p-4">
                <p className="font-semibold text-slate-800">{s.label}</p>
                <p className="mt-1 text-sm text-slate-500">{s.detail}</p>
              </div>
            ))}
          </div>

          {history.length > 0 && (
            <div className="mt-10 rounded-2xl border border-slate-200 bg-white p-6">
              <h3 className="text-lg font-bold text-slate-800">Chequeos anteriores</h3>
              <div className="mt-4 divide-y">
                {history.map((h) => (
                  <button
                    key={h.id}
                    onClick={() => loadCheck(h.id)}
                    data-testid={`assisted-history-${h.id}`}
                    className="flex w-full items-center justify-between py-3 text-left hover:bg-slate-50"
                  >
                    <div>
                      <p className="font-medium text-slate-800">{h.target_host}</p>
                      <p className="text-xs text-slate-500">
                        {new Date(h.created_at).toLocaleString('es-AR')}
                      </p>
                    </div>
                    <span className="flex items-center gap-3 text-sm text-slate-600">
                      {h.status === 'done' ? (h.report?.nota || 'Listo') : h.status === 'running' ? 'En curso' : 'Interrumpido'}
                      {h.status === 'done' && (
                        <span className="rounded-full bg-sky-50 px-3 py-1 text-xs font-semibold text-sky-700">
                          Ver informe →
                        </span>
                      )}
                    </span>
                  </button>
                ))}
              </div>
            </div>
          )}
        </div>
      )}

      {/* ---------- Antes de arrancar ---------- */}
      {view === 'preflight' && (
        <div className="no-print mx-auto max-w-4xl" data-testid="assisted-preflight">
          <h2 className="text-3xl font-bold text-slate-800">Antes de arrancar</h2>
          <p className="mt-2 text-slate-600">Dos definiciones y empezamos.</p>

          <div className="mt-8 rounded-2xl border border-slate-200 bg-white p-6">
            <h3 className="font-bold text-slate-800">1. ¿Qué querés revisar?</h3>
            <select
              value={targetId}
              onChange={(e) => setTargetId(e.target.value)}
              data-testid="assisted-target-select"
              className="mt-3 w-full rounded-lg border border-slate-300 px-4 py-3 text-slate-800"
            >
              {status.targets.map((t) => (
                <option key={t.id} value={t.id}>{t.name} — {t.host}</option>
              ))}
            </select>
            {status.targets.length === 0 && (
              <p className="mt-3 text-sm text-rose-600" data-testid="assisted-no-targets">
                Todavía no cargaste ningún objetivo. Andá a Objetivos, agregá tu dominio y firmá la
                autorización; después volvé acá.
              </p>
            )}
          </div>

          <div className="mt-6 rounded-2xl border border-slate-200 bg-white p-6">
            <h3 className="font-bold text-slate-800">2. ¿Hasta dónde querés que busquemos?</h3>
            <p className="mt-1 text-sm text-slate-500">
              Tus sistemas se comunican por "puertas" numeradas. Cuantas más revisemos, más completo
              es el chequeo y más tarda.
            </p>
            <div className="mt-4 grid gap-4 md:grid-cols-3">
              {status.port_profiles.map((p) => (
                <button
                  key={p.id}
                  onClick={() => setProfile(p.id)}
                  data-testid={`assisted-profile-${p.id}`}
                  className={`rounded-xl border-2 p-4 text-left transition ${
                    profile === p.id
                      ? 'border-sky-500 bg-sky-50 ring-2 ring-sky-200'
                      : 'border-slate-200 hover:border-slate-300'
                  }`}
                >
                  <div className="flex items-center justify-between">
                    <span className="font-bold text-slate-800">{p.name}</span>
                    {p.id === 'aplicaciones' && (
                      <span className="rounded-full bg-sky-600 px-2 py-0.5 text-[10px] font-bold text-white">
                        SUGERIDO
                      </span>
                    )}
                  </div>
                  <p className="mt-1 text-xs font-medium text-slate-500">{p.subtitle}</p>
                  <p className="mt-2 text-sm text-slate-600">{p.why}</p>
                  <p className="mt-3 text-xs font-semibold text-slate-700">⏱ {p.minutes}</p>
                </button>
              ))}
            </div>
          </div>

          <div className="mt-6 rounded-2xl border-2 border-amber-300 bg-amber-50 p-6" data-testid="assisted-time-warning">
            <h3 className="font-bold text-amber-900">Tené en cuenta antes de empezar</h3>
            <ul className="mt-3 space-y-2 text-sm text-amber-900">
              <li>• Con la opción elegida, el chequeo completo va a tardar aproximadamente <strong>{esperado}</strong>.</li>
              <li>• Podés cerrar esta pantalla: el chequeo sigue corriendo y al volver vas a ver el avance.</li>
              <li>• Consume <strong>un solo escaneo</strong> de tu plan, aunque por dentro corran {status.steps.length} revisiones.</li>
              <li>• Las pruebas son de detección, no tocan ni modifican nada en tus sistemas.</li>
            </ul>
          </div>

          {error && <p className="mt-4 rounded-lg bg-rose-50 p-3 text-sm text-rose-700" data-testid="assisted-error">{error}</p>}

          <div className="mt-8 flex gap-3">
            <button
              onClick={start}
              disabled={starting || !targetId}
              data-testid="assisted-start-btn"
              className="rounded-full bg-emerald-600 px-10 py-4 text-lg font-bold text-white shadow-lg transition hover:bg-emerald-700 disabled:opacity-50"
            >
              {starting ? 'Arrancando...' : 'Sí, arrancá el chequeo'}
            </button>
            <button
              onClick={() => setView('intro')}
              data-testid="assisted-back-btn"
              className="rounded-full border border-slate-300 px-8 py-4 font-semibold text-slate-600 hover:bg-slate-50"
            >
              Volver
            </button>
          </div>
        </div>
      )}

      {/* ---------- En ejecución ---------- */}
      {view === 'running' && check && (
        <div data-testid="assisted-running">
          <div className="rounded-2xl bg-gradient-to-r from-slate-900 to-blue-900 px-8 py-7 text-white shadow-lg">
            <div className="flex flex-wrap items-center justify-between gap-4">
              <div>
                <p className="text-xs uppercase tracking-widest text-sky-300">Chequeo en curso</p>
                <h2 className="mt-1 text-2xl font-bold">{check.target_host}</h2>
              </div>
              <div className="text-right">
                <p className="text-4xl font-extrabold" data-testid="assisted-progress-pct">{avance}%</p>
                <p className="text-xs text-slate-300">{hechos} de {pasos.length} revisiones</p>
              </div>
            </div>
            <div className="mt-5 h-3 overflow-hidden rounded-full bg-white/15">
              <div className="h-full rounded-full bg-gradient-to-r from-sky-400 to-emerald-400 transition-all duration-700"
                   style={{ width: `${avance}%` }} />
            </div>
            <button
              onClick={() => { setCheck(null); setView('intro'); loadStatus(); }}
              data-testid="assisted-running-back"
              className="mt-5 text-xs font-semibold text-sky-300 underline-offset-2 hover:underline"
            >
              ← Dejarlo corriendo y ver mis informes anteriores
            </button>
          </div>

          <div className="mt-6 grid gap-6 lg:grid-cols-5">
            <div className="lg:col-span-3 rounded-2xl border border-slate-200 bg-white p-6">
              <h3 className="font-bold text-slate-800">Qué estamos revisando</h3>
              <ol className="mt-4 space-y-2">
                {pasos.map((s, i) => (
                  <li
                    key={s.key}
                    data-testid={`assisted-step-${s.key}`}
                    className={`flex items-start gap-3 rounded-xl px-3 py-2.5 transition ${
                      s.status === 'running' ? 'bg-sky-50 ring-1 ring-sky-200' : ''
                    }`}
                  >
                    <span className={`mt-0.5 flex h-7 w-7 shrink-0 items-center justify-center rounded-full text-sm font-bold ${
                      s.status === 'done' ? 'bg-emerald-100 text-emerald-700'
                        : s.status === 'running' ? 'animate-pulse bg-sky-500 text-white'
                        : s.status === 'skipped' ? 'bg-slate-100 text-slate-400'
                        : s.status === 'failed' ? 'bg-rose-100 text-rose-700'
                        : 'bg-slate-100 text-slate-400'
                    }`}>
                      {STEP_ICON[s.status] || i + 1}
                    </span>
                    <div className="min-w-0">
                      <p className={`font-medium ${s.status === 'pending' ? 'text-slate-400' : 'text-slate-800'}`}>
                        {s.label}
                      </p>
                      {s.status === 'running' && (
                        <>
                          <p className="text-xs text-sky-700">{s.detail}</p>
                          <p className="text-xs font-medium text-slate-500">
                            Hace {minutosDesde(s.started_at)}
                            {minutosDesde(s.started_at) !== '1 minuto' && ' · esta revisión puede tardar varios minutos'}
                          </p>
                        </>
                      )}
                      {s.status === 'done' && s.findings > 0 && (
                        <p className="text-xs text-amber-700">{s.findings} hallazgo(s)</p>
                      )}
                      {s.status === 'done' && s.findings === 0 && (
                        <p className="text-xs text-emerald-700">Sin hallazgos</p>
                      )}
                      {['skipped', 'failed'].includes(s.status) && (
                        <p className="text-xs text-slate-500">No se pudo completar</p>
                      )}
                    </div>
                  </li>
                ))}
              </ol>
            </div>

            <div className="lg:col-span-2 flex flex-col rounded-2xl bg-slate-900 p-5 shadow-inner">
              <h3 className="text-sm font-bold uppercase tracking-wide text-slate-400">En vivo</h3>
              <div ref={feedRef} className="mt-3 h-80 overflow-y-auto font-mono text-xs leading-relaxed"
                   data-testid="assisted-feed">
                {(check.feed || []).map((f, i) => (
                  <p key={i} className={FEED_STYLE[f.level] || FEED_STYLE.info}>
                    <span className="text-slate-600">
                      {new Date(f.ts).toLocaleTimeString('es-AR')}{' '}
                    </span>
                    {f.text}
                  </p>
                ))}
                {(check.feed || []).length === 0 && <p className="text-slate-500">Iniciando...</p>}
              </div>
              <p className="mt-3 border-t border-slate-700 pt-3 text-xs text-slate-400">
                Podés irte y volver: el chequeo sigue corriendo en nuestros servidores.
              </p>
            </div>
          </div>
        </div>
      )}

      {/* ---------- Informe final ---------- */}
      {view === 'report' && check?.report && (
        <div>
          <div className="no-print mb-6 flex flex-wrap items-center justify-between gap-3">
            <button onClick={() => { setCheck(null); setView('intro'); loadStatus(); }}
                    data-testid="assisted-report-back"
                    className="rounded-full border border-slate-300 px-6 py-2.5 font-semibold text-slate-600 hover:bg-slate-50">
              ← Volver
            </button>
            <button onClick={() => window.print()} data-testid="assisted-print-btn"
                    className="rounded-full bg-slate-900 px-8 py-3 font-bold text-white shadow hover:bg-slate-800">
              🖨 Imprimir informe
            </button>
          </div>

          <div id="assisted-report" className="rounded-2xl border border-slate-200 bg-white p-8 shadow-sm">
            <div className="flex flex-wrap items-start justify-between gap-4 border-b pb-6">
              <div>
                <p className="text-xs font-bold uppercase tracking-widest text-sky-600">SegurIA · Chequeo de seguridad</p>
                <h1 className="mt-2 text-3xl font-extrabold text-slate-900">{check.report.company_name}</h1>
                <p className="mt-1 text-slate-600">Revisamos <strong>{check.report.target_host}</strong></p>
              </div>
              <p className="text-sm text-slate-500">
                {new Date(check.report.generated_at).toLocaleString('es-AR')}
              </p>
            </div>

            <div className={`mt-6 rounded-2xl p-6 ring-2 ${(COLOR_BAND[check.report.color] || COLOR_BAND.verde).soft} ${(COLOR_BAND[check.report.color] || COLOR_BAND.verde).ring}`}
                 data-testid="assisted-report-verdict">
              <h2 className={`text-2xl font-bold ${(COLOR_BAND[check.report.color] || COLOR_BAND.verde).text}`}>
                Terminé. {check.report.nota}
              </h2>
              <p className="mt-2 text-slate-700">{check.report.resumen}</p>
            </div>

            <div className="mt-6 grid grid-cols-2 gap-3 sm:grid-cols-5">
              <Counter label="urgentes" value={check.report.totales.criticas} tone="border-rose-200 bg-rose-50 text-rose-700" />
              <Counter label="importantes" value={check.report.totales.altas} tone="border-amber-200 bg-amber-50 text-amber-700" />
              <Counter label="medias" value={check.report.totales.medias} tone="border-yellow-200 bg-yellow-50 text-yellow-700" />
              <Counter label="menores" value={check.report.totales.bajas} tone="border-slate-200 bg-slate-50 text-slate-700" />
              <Counter label="informativas" value={check.report.totales.informativas} tone="border-sky-200 bg-sky-50 text-sky-700" />
            </div>

            <h3 className="mt-10 text-xl font-bold text-slate-900">Qué revisamos</h3>
            <div className="mt-3 overflow-hidden rounded-xl border border-slate-200">
              {check.report.que_revisamos.map((r, i) => (
                <div key={i} className={`flex items-start justify-between gap-4 px-4 py-3 ${i % 2 ? 'bg-slate-50' : ''}`}>
                  <div>
                    <p className="font-medium text-slate-800">{r.label}</p>
                    <p className="text-sm text-slate-500">{r.detail}</p>
                  </div>
                  <span className="shrink-0 text-sm font-semibold text-slate-600">
                    {r.status === 'done'
                      ? (r.findings > 0 ? `${r.findings} hallazgo(s)` : 'Sin hallazgos')
                      : 'No evaluado'}
                  </span>
                </div>
              ))}
            </div>

            {check.report.hallazgos_principales.length > 0 && (
              <>
                <h3 className="mt-10 text-xl font-bold text-slate-900">Lo más importante que encontramos</h3>
                <div className="mt-3 space-y-4" data-testid="assisted-report-findings">
                  {check.report.hallazgos_principales.map((h, i) => (
                    <div key={i} className="rounded-xl border border-slate-200 p-4">
                      <div className="flex flex-wrap items-center gap-2">
                        <span className={`rounded-full border px-3 py-0.5 text-xs font-bold ${GRAVEDAD_STYLE[h.bucket] || GRAVEDAD_STYLE.medias}`}>
                          {h.gravedad}
                        </span>
                        <p className="font-semibold text-slate-900">{h.titulo}</p>
                        {h.veces > 1 && (
                          <span className="rounded-full bg-slate-100 px-2 py-0.5 text-xs text-slate-600">
                            detectado {h.veces} veces
                          </span>
                        )}
                        {h.donde && <span className="text-xs text-slate-500">{h.donde}</span>}
                      </div>
                      {h.que_significa && (
                        <p className="mt-2 text-sm text-slate-600"><strong>Qué significa:</strong> {h.que_significa}</p>
                      )}
                      <p className="mt-1 text-sm text-slate-700"><strong>Qué hacer:</strong> {h.que_hacer}</p>
                      {h.detalle_tecnico && h.detalle_tecnico !== h.titulo && (
                        <p className="mt-2 text-xs text-slate-400">
                          Dato técnico para tu proveedor: {h.detalle_tecnico}{h.cve ? ` (${h.cve})` : ''}
                        </p>
                      )}
                    </div>
                  ))}
                </div>
              </>
            )}

            <h3 className="mt-10 text-xl font-bold text-slate-900">Próximos pasos</h3>
            <ul className="mt-3 space-y-2">
              {check.report.proximos_pasos.map((p, i) => (
                <li key={i} className="flex gap-2 text-slate-700">
                  <span className="font-bold text-sky-600">{i + 1}.</span>{p}
                </li>
              ))}
            </ul>

            <p className="mt-10 border-t pt-4 text-xs text-slate-400">
              Informe generado automáticamente por SegurIA sobre pruebas de detección no intrusivas,
              realizadas con autorización expresa del titular del objetivo.
            </p>
          </div>
        </div>
      )}

      {view === 'report' && check && !check.report && (
        <div className="rounded-2xl border border-rose-200 bg-rose-50 p-8 text-center" data-testid="assisted-failed">
          <h2 className="text-xl font-bold text-rose-800">El chequeo se interrumpió</h2>
          <p className="mt-2 text-rose-700">Volvé a intentarlo. Si sigue pasando, avisanos.</p>
          <button onClick={() => { setCheck(null); setView('intro'); }}
                  className="mt-5 rounded-full bg-rose-600 px-6 py-2.5 font-semibold text-white">
            Volver
          </button>
        </div>
      )}
    </div>
  );
};

export default AssistedCheck;
